/**
 * Welle 8c-pre38/41: Kunden-Overlay während des PDF-Signiervorgangs.
 *
 * Der Signiervorgang läuft über die lokale SignController-API am Signotec-Delta-Display
 * (nicht im Browser). Dieses Overlay zeigt dem Kunden:
 *  - während der Übertragung des Dokuments zur Stele: Fortschritt in %
 *  - während des Signierens: die Bedien-Anleitung
 *  - während der Rücksendung des unterschriebenen Dokuments: Fortschritt in %
 * Gesteuert über die Window-Events 'bamoa:sign-instructions' (Anleitung an/aus) und
 * 'bamoa:sign-progress' (Phase 'receiving' | 'returning' + Prozent).
 */
import { useEffect, useState } from 'react';

type Phase = 'idle' | 'receiving' | 'signing' | 'returning';

export function SignatureOverlay() {
  const [phase, setPhase] = useState<Phase>('idle');
  const [pct, setPct] = useState(0);

  useEffect(() => {
    const onInstr = (e: Event) => {
      const d = (e as CustomEvent).detail as { show?: boolean };
      if (d?.show) setPhase((p) => (p === 'returning' ? p : 'signing'));
      else { setPhase('idle'); setPct(0); }
    };
    const onProgress = (e: Event) => {
      const d = (e as CustomEvent).detail as { phase?: 'receiving' | 'returning'; pct?: number };
      if (d?.phase === 'receiving') { setPhase('receiving'); setPct(d.pct ?? 0); }
      else if (d?.phase === 'returning') { setPhase('returning'); setPct(d.pct ?? 0); }
    };
    window.addEventListener('bamoa:sign-instructions', onInstr);
    window.addEventListener('bamoa:sign-progress', onProgress);
    return () => {
      window.removeEventListener('bamoa:sign-instructions', onInstr);
      window.removeEventListener('bamoa:sign-progress', onProgress);
    };
  }, []);

  if (phase === 'idle') return null;

  return (
    <div style={overlay}>
      <div style={card}>
        {phase === 'signing' ? (
          <>
            <div style={heading}>Zum Unterschreiben</div>
            <ul style={list}>
              <li style={item}>Bitte nehmen Sie den Stift und scrollen das Dokument runter zum Unterschriftenfeld</li>
              <li style={item}>tippen Sie auf den <b>grünen Stift</b>,</li>
              <li style={item}>unterschreiben und</li>
              <li style={item}>bestätigen Sie Ihre Unterschrift mit dem <b>grünen Haken</b> (Symbol).</li>
              <li style={item}>Zum Versand des Dokumentes klicken Sie oben rechts auf die <b>„rote Tür"</b> (Symbol)</li>
            </ul>
            <div style={spinnerRow}>
              <span style={spinner} />
              <span style={waitText}>Bitte am Unterschriften-Display fortfahren…</span>
            </div>
          </>
        ) : (
          <>
            <div style={heading}>
              {phase === 'receiving' ? 'Dokument wird übertragen…' : 'Unterschriebenes Dokument wird gesendet…'}
            </div>
            <div style={barOuter}>
              <div style={{ ...barInner, width: `${pct}%` }} />
            </div>
            <div style={waitText}>{pct}%</div>
          </>
        )}
      </div>
      <style>{`@keyframes bamoa-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 9000,
  background: 'rgba(8,8,10,0.82)', backdropFilter: 'blur(6px)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '6vw',
};
const card: React.CSSProperties = {
  width: '100%', maxWidth: 860, background: '#15151a', borderRadius: 22,
  padding: '36px 40px', boxShadow: '0 24px 80px rgba(0,0,0,0.6)',
  display: 'flex', flexDirection: 'column', gap: 18,
};
const heading: React.CSSProperties = { color: '#fff', fontSize: 34, fontWeight: 700, letterSpacing: 0.2 };
const list: React.CSSProperties = { margin: 0, paddingLeft: 28, display: 'flex', flexDirection: 'column', gap: 14 };
const item: React.CSSProperties = { color: 'rgba(255,255,255,0.92)', fontSize: 24, lineHeight: 1.35 };
const spinnerRow: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 16, marginTop: 10 };
const spinner: React.CSSProperties = {
  width: 26, height: 26, borderRadius: '50%',
  border: '3px solid rgba(255,255,255,0.2)', borderTopColor: '#fff',
  animation: 'bamoa-spin 0.9s linear infinite', display: 'inline-block',
};
const waitText: React.CSSProperties = { color: 'rgba(255,255,255,0.6)', fontSize: 20 };
const barOuter: React.CSSProperties = {
  width: '100%', height: 14, borderRadius: 999, background: 'rgba(255,255,255,0.15)', overflow: 'hidden',
};
const barInner: React.CSSProperties = {
  height: '100%', background: '#22c55e', borderRadius: 999, transition: 'width 0.15s ease',
};
